import { useEffect, useState } from "react";
import AuiIcon from "../../components/AuiDropdown/AuiIcon";
import AuiButton from "../../components/Buttons/Buttons";
import OrganizationFilterSection, {
  DropdownOption,
} from "../../components/Filters/OrganizationFilterSection";
import Headings from "../../components/Headings/Headings";
import MoreOption from "../../components/MoreOptions/MoreOption";
import "./OrganizationListing.scss";
import { Link, useNavigate } from "react-router-dom";
import {
  getOrganisationList,
  getOrganisationListExport,
  OrganisationListParams,
} from "../../services/training-session.service";
import noRecord from "../../assets/images/no-recs.png";
import AuiPrimaryPagination from "../../components/AuiDropdown/AuiPagination";
import DropDown from "../../components/DropDown/DropDown";
import AuiSortBy from "../../components/AuiDropdown/AuiSortByDropdown";
import { formatDate } from "../../components/Hooks/dateTimeHooks";

const OrganizationListing = () => {
  const navigate = useNavigate();
  const [organisationsList, setOrganisationsList] = useState<any[]>([]);
  const [orgType, setOrgType] = useState<DropdownOption | null>(null);
  const [country, setCountry] = useState<DropdownOption | null>(null);
  const [state, setState] = useState<DropdownOption | null>(null);
  const [region, setRegion] = useState<DropdownOption | null>(null);
  const [
    selectedSortOption,
    setSelectedSortOption,
  ] = useState<DropdownOption | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const pageOrder = "desc";
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    isFirst: true,
    isLast: false,
    pageNumber: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
    rowsperpage: 10,
  });
  const sortOptions = [
    { id: 1, name: "Country" },
    { id: 2, name: "State" },
    { id: 3, name: "Region" },
    { id: 4, name: "Date" },
  ];

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const getOrgList = async () => {
    setIsLoading(true);
    setOrganisationsList([]);
    try {
      const params: OrganisationListParams = {
        type:
          orgType?.name?.split(" ")[0]?.toUpperCase() === "CERTIFYING"
            ? "APPROVING"
            : orgType?.name?.split(" ")[0]?.toUpperCase(),
        state: state?.id,
        country: country?.id,
        region: region?.regionId,
        sortBy: selectedSortOption?.name?.toLowerCase() ?? "",
        pageNumber: currentPage,
        pageSize,
        orderBy: sortOrder || "asc",
      };

      const result = await getOrganisationList(params);
      const organisationData = result?.data?.organisations || [];
      setOrganisationsList(organisationData);
      setPagination({
        isFirst: !!result?._pagination?.isFirst,
        isLast: result?._pagination?.isLast || false,
        totalItems: result?._pagination?.totalCount || 0,
        totalPages: result?._pagination?.totalPages || 0,
        pageNumber: result?._pagination?.pageNumber || 1,
        pageSize: result?._pagination?.pageSize || 10,
        rowsperpage: result?._pagination?.pageSize || 10,
      });
    } catch (error) {
      console.error("Error fetching organization list:", error);
      setOrganisationsList([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getOrgList();
  }, [
    currentPage,
    pageSize,
    orgType,
    country,
    state,
    region,
    pageOrder,
    sortOrder,
    selectedSortOption,
  ]);

  const handleDropDownChange = (value: string) => {
    const selectedOption =
      sortOptions.find((option) => option.name === value) || null;
    setSelectedSortOption(selectedOption);
    setCurrentPage(1);
  };

  const toggleOrder = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    setCurrentPage(1);
  };

  const handleViewClick = (id: number) => {
    navigate(`/organization-details/${id}`);
  };

  const exportOrgExcel = () => {
    exportOrgList();
  };

  const exportOrgList = async () => {
    try {
      const params: OrganisationListParams = {
        type:
          orgType?.name?.split(" ")[0]?.toUpperCase() === "CERTIFYING"
            ? "APPROVING"
            : orgType?.name?.split(" ")[0]?.toUpperCase(),
        state: state?.id,
        country: country?.id,
        region: region?.regionId,
        sortBy: selectedSortOption?.name?.toLowerCase() ?? "",
        orderBy: sortOrder || "asc",
      };

      const result = await getOrganisationListExport(params);

      if (result?.status === 200 && result.data?.Data?.url) {
        window.open(result.data.Data.url, "_self");
      }
    } catch (error) {
      console.error("Error while fetching org list:", error);
    }
  };

  return (
    <div className="OrganizationMainSection container my-4">
      <div className="border-bottom">
        <div className="row d-flex align-items-center my-3">
          <div className="col-6">
            <Headings size="h1" className="mb-0">
              Organizations
            </Headings>
          </div>
          <div className="col-6">
            <div className="d-flex justify-content-end">
              <Link
                to="/organization-creation"
                className="btn btn-primary btn-md btn-round newCoursebtn"
              >
                <aui-icon icon="plus" svgwidth="15" svgheight="15"></aui-icon>
                New Organization
              </Link>
            </div>
          </div>
        </div>
      </div>
      <OrganizationFilterSection
        setOrgType={setOrgType}
        setCountry={setCountry}
        setState={setState}
        setRegion={setRegion}
        setCurrentPage={setCurrentPage}
      ></OrganizationFilterSection>
      <div className="orgTableSection">
        <div className="row my-3 mr-0 align-items-center">
          <div className="col-12 col-md-6 col-lg-8">
           <output className="item-count" aria-live="polite">
            {organisationsList.length
              ? `Showing ${(currentPage - 1) * pageSize + 1} to ${Math.min(
                  currentPage * pageSize,
                  pagination.totalItems
                )} items out of ${pagination.totalItems}`
              : ""}
          </output>
          </div>
          <div className="col-12 col-md-6 col-lg-4">
            <div className="d-flex align-items-center justify-content-end flex-nowrap gap-3">
              {organisationsList.length > 0 && (
                <div className="px-3 py-2 d-flex align-items-center cursor-pointer mt-n2">
                  <aui-icon
                    block={true}
                    svgclass="aui-icon-export"
                    pathclass="aui-path-export"
                    icon="export"
                    svgwidth="20"
                    svgheight="20"
                  ></aui-icon>
                  <button
                    type="button"
                    className="export-text export-btn"
                    onClick={exportOrgExcel}
                  >
                    Export
                  </button>
                </div>
              )}
              <div className="d-flex align-items-center flex-nowrap sort-section">
                <DropDown
                  listInBtn
                  view="horizontal"
                  dropdownLabel="Sort By: "
                  data={sortOptions.map((v: any) => v.name)}
                  placeHolder="None"
                  onChange={handleDropDownChange}
                />
                <AuiSortBy
                  activecolor="#0d7712"
                  inactivecolor="#909090"
                  width={28}
                  height={30}
                  svgclass="custom-svg-class"
                  onClick={toggleOrder}
                />
              </div>
            </div>
          </div>
        </div>
        {isLoading && (
          <div className="aui-loader loaderHeight">
            <aui-loader type="content"></aui-loader>
          </div>
        )}
        {!isLoading &&
          (organisationsList.length > 0 ? (
            <>
              <h2 className="sr-only" id="orgListing-title">List of Organizations with Type, Location, and Creation Date</h2>
              <section className="orgListingTable" aria-labelledby="orgListing-title">
              <aui-table
                type="status"
                headerbackground={true}
                headerborder={true}
              >
                <table>
                  <thead>
                    <tr>
                      <th scope="col">
                        <div className="aui-th font-600">Organizations</div>
                      </th>
                      <th scope="col">
                        <div className="aui-th font-600">Type</div>
                      </th>
                      <th scope="col">
                        <div className="aui-th font-600">Country</div>
                      </th>
                      <th scope="col">
                        <div className="aui-th font-600">Region</div>
                      </th>
                      <th scope="col">
                        <div className="aui-th font-600">State</div>
                      </th>
                      <th scope="col">
                        <div className="aui-th font-600">Date created</div>
                      </th>
                      <th scope="col">
                        <div className="aui-th d-flex justify-content-lg-center pl-3 font-600">
                          Actions
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {organisationsList.map((org) => (
                      <tr className="aui-tr-normal orgTableRow" key={`org-${org.createdAt}`}>
                        <td data-title="Organizations">
                          <div className="aui-td">{org.name}</div>
                        </td>
                        <td data-title="Type">
                          <div className="aui-td">{org.type.charAt(0).toUpperCase() + org.type.slice(1).toLowerCase()}</div>
                        </td>
                        <td data-title="Country">
                          <div className="aui-td">
                            {org.organisationGeographies.countryName}
                          </div>
                        </td>
                        <td data-title="Region">
                          <div className="aui-td">
                            {org.organisationGeographies.regionName}
                          </div>
                        </td>
                        <td data-title="State">
                          <div className="aui-td">
                            {org.organisationGeographies.stateName}
                          </div>
                        </td>
                        <td data-title="Date created">
                          <div className="aui-td">
                            {formatDate(org.createdAt)}
                          </div>
                        </td>
                        <td data-title="Action">
                          <div className="d-flex position-relative aui-td justify-content-lg-center">
                            <MoreOption  ariaLabel={`${org.name} More options`}>
                              <ul className="px-1">
                                <li>
                                  <AuiIcon
                                    iconClass="view"
                                    svgheight="15"
                                    svgwidth="15"
                                  />
                                  <AuiButton
                                    variant="button-text-styled"
                                    buttontitle="View"
                                    modalid="exampleModal"
                                    size="small"
                                    buttonid={""}
                                    buttonclass="actionBtn px-2"
                                    buttonClick={() => handleViewClick(org?.id)}
                                  ></AuiButton>
                                </li>
                              </ul>
                            </MoreOption>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </aui-table>
            </section>
          </>
          ) : (
            <output
              className="aui-status-table-emptystate aui-table-empty d-block"
              aria-live="polite"
            >
              <div
                className="aui-table-no-data d-flex flex-column justify-content-center align-items-center"
                role="alert"
                aria-label="No data found for organizations list"
              >
                <img src={noRecord} alt="Illustration indicating no data available" />
                <h2 className="mb-0" id="no-data-heading">No Data Found</h2>
              </div>
            </output>
          ))}

        {organisationsList.length ? (
          <section className="orgPagination py-4">
            <AuiPrimaryPagination
              inputdata={pagination}
              totalitems={pagination.totalItems}
              rowsperpage={pagination.rowsperpage}
              setCurrentPage={setCurrentPage}
              setPageSize={handlePageSizeChange}
            />
          </section>
        ) : null}
      </div>
    </div>
  );
};

export default OrganizationListing;
